// src/components/WOEPayment/services/validationService.js

import { getCurrentUser } from '../../../../utils/auth'; // ⚠️ double-check this relative path matches your project structure
import { normalizeOrderType } from '../../../../utils/orderTypeUtils';
import { isIsraelUser } from '../../../../utils/locationUtils';

// Exempt from the Wig Repair warranty / $0-total gate: 'chenw', and all
// Israel-location users. Keep this in sync with the same check in WOEWigDetails.jsx.
const isWarrantyGateExempt = () =>
    String(getCurrentUser() || '').trim().toLowerCase() === 'chenw' ||
    isIsraelUser();

const validationService = {
    
    // Main order validation
    validateOrder: ({
        paymentCalculations,
        agreementManager,
        customerInfo,
        isIsraelUser,
        orderItems,
        accessories,
        depositBypassActive = false
    }) => {
        console.log('🔍 === VALIDATION DEBUG ===');
          // Check items first
        const itemValidation = validationService.validateOrderHasItems({
            orderItems,
            accessories
        });
        
        if (!itemValidation.isValid) {
            return itemValidation;
        }

        // Wig Repair backstop: a repair order must charge something OR carry an
        // explicit repair line item (a $0 line is how a warranty-covered repair
        // is recorded). The primary gate lives on the Wig Details step; this
        // catches drafts that reached payment without passing it.
        const repairGate = validationService.validateWigRepairHasCharge({
            customerInfo, orderItems, accessories,
        });
        if (!repairGate.isValid) {
            return repairGate;
        }

        // Check payment requirements
        const paymentValidation = validationService.validatePayments({
            paymentCalculations,
            customerInfo,
            depositBypassActive,
            isIsraelUser
        });
        
        if (!paymentValidation.isValid) {
            return paymentValidation;
        }
        
        // Check tax requirements
        const taxValidation = validationService.validateTaxRequirements({
            paymentCalculations,
            customerInfo,
            isIsraelUser
        });
        
        if (!taxValidation.isValid) {
            return taxValidation;
        }
        
        // Check agreement requirements
        const agreementValidation = validationService.validateAgreement({
            agreementManager,
            customerInfo
        });
        
        if (!agreementValidation.isValid) {
            return agreementValidation;
        }
        
        console.log('✅ All validations passed');
        return { isValid: true };
    },
    
    // Payment validation
    validatePayments: ({ paymentCalculations, customerInfo, depositBypassActive = false, isIsraelUser = false }) => {
        console.log('🔍 Validating payments...');

        if (depositBypassActive) {
            console.log('✅ Deposit bypass active — skipping payment requirement check');
            return { isValid: true };
        }

        if (isIsraelUser) {
            console.log('✅ Israel user — deposit requirement bypassed');
            return { isValid: true };
        }

        if (!paymentCalculations.paymentRequirementMet) {
            console.log('❌ Payment requirement not met');
            
            let errorMessage = 'Minimum payment requirement not met.';

            // "Aidel New Wig" / "Aidel Wig Repair" follow the same rules as their base types.
            const baseOrderType = normalizeOrderType(customerInfo?.orderType);

            if (baseOrderType === 'New Wig') {
                errorMessage = 'New Wig orders require a minimum payment of $2,500 to proceed.';
            } else if (baseOrderType === 'Wig Repair' && parseFloat(paymentCalculations.totals.total) > 1000) {
                const minPayment = (parseFloat(paymentCalculations.totals.total) * 0.5).toFixed(2);
                errorMessage = `Wig Repair orders over $1,000 require a minimum payment of ${minPayment} (50% of total) to proceed.`;
            } else if (baseOrderType === 'New Sale Wig') {
                const expectedTotal = parseFloat(paymentCalculations.totals.total) + parseFloat(paymentCalculations.paymentSummary.totalCardFees || 0);
                errorMessage = `New Sale Wig orders require full payment of ${expectedTotal.toFixed(2)} to proceed.`;
            }
            
            return {
                isValid: false,
                error: errorMessage,
                category: 'payment'
            };
        }
        
        console.log('✅ Payment validation passed');
        return { isValid: true };
    },
    
    // Tax validation
    validateTaxRequirements: ({ paymentCalculations, customerInfo, isIsraelUser }) => {
        console.log('🔍 Validating tax requirements...');
        
        if (!paymentCalculations.taxRequirementMet) {
            console.log('❌ Tax requirement not met');
            
            let errorMessage = 'Tax location selection required before proceeding.';
            
            if (isIsraelUser) {
                // Israel users should never fail tax validation
                console.log('✅ Israel user - tax validation skipped');
                return { isValid: true };
            }
            
            const baseOrderType = normalizeOrderType(customerInfo?.orderType);
            if (baseOrderType === 'New Wig' || baseOrderType === 'New Sale Wig') {
                errorMessage = 'For tax-eligible orders, you must select a tax location before proceeding.';
            }
            
            return {
                isValid: false,
                error: errorMessage,
                action: 'showTaxSelector',
                category: 'tax'
            };
        }
        
        console.log('✅ Tax validation passed');
        return { isValid: true };
    },
    
    // Agreement validation
    validateAgreement: ({ agreementManager, customerInfo }) => {
        console.log('🔍 Validating agreement requirements...');
        
        const agreementRequired = agreementManager.isRequired;
        
        console.log('🔍 Agreement validation details:', {
            required: agreementRequired,
            signed: agreementManager.isSigned,
            orderType: customerInfo?.orderType,
            agreementData: agreementManager.agreementState.signedData,
            status: agreementManager.agreementState.status,
            signingMethod: agreementManager.agreementState.signingMethod
        });
        
        if (agreementRequired && !agreementManager.isSigned) {
            console.log('❌ Customer agreement validation failed');
            
            let errorMessage = 'Customer agreement must be signed before proceeding.';
            let action = 'showAgreement';
            
            // Check if SMS is in progress
            if (agreementManager.isWaitingForSMS) {
                errorMessage = 'Waiting for customer to sign agreement via SMS. You can also sign in-person as a backup.';
                action = null; // Don't auto-open modal if SMS is pending
            }
            
            // Check if SMS failed or expired
            if (agreementManager.hasFailed || agreementManager.hasExpired) {
                errorMessage = 'SMS agreement failed or expired. Please sign in-person or retry SMS.';
                action = 'showAgreement';
            }
            
            return {
                isValid: false,
                error: errorMessage,
                action: action,
                category: 'agreement'
            };
        }
        
        console.log('✅ Agreement validation passed');
        return { isValid: true };
    },
    
    // Validate specific payment method
    validatePaymentMethod: (payment) => {
        const errors = [];
        
        if (!payment.method) {
            errors.push('Payment method is required');
        }
        
        const amount = parseFloat(payment.amount);
        if (isNaN(amount) || amount <= 0) {
            errors.push('Payment amount must be greater than 0');
        }
        
        if (payment.method === 'Check' && !payment.notes) {
            errors.push('Check number is required for check payments');
        }
        
        return {
            isValid: errors.length === 0,
            errors: errors
        };
    },
    
    // Validate all payment methods
    validateAllPayments: (payments) => {
        const results = payments.map((payment, index) => ({
            index,
            payment,
            validation: validationService.validatePaymentMethod(payment)
        }));
        
        const invalidPayments = results.filter(r => !r.validation.isValid);
        
        if (invalidPayments.length > 0) {
            return {
                isValid: false,
                errors: invalidPayments.map(p => 
                    `Payment ${p.index + 1}: ${p.validation.errors.join(', ')}`
                )
            };
        }
        
        return { isValid: true };
    },
    
    // Validate customer information
    validateCustomerInfo: (customerInfo) => {
        const errors = [];
        
        if (!customerInfo?.customerName?.trim()) {
            errors.push('Customer name is required');
        }
        
        if (!customerInfo?.orderType) {
            errors.push('Order type is required');
        }
        
        if (customerInfo?.email && !validationService.isValidEmail(customerInfo.email)) {
            errors.push('Invalid email format');
        }
        
        if (customerInfo?.phone && !validationService.isValidPhone(customerInfo.phone)) {
            errors.push('Invalid phone format');
        }
        
        return {
            isValid: errors.length === 0,
            errors: errors
        };
    },
    
    // Email validation
    isValidEmail: (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    },
    
    // Phone validation
    isValidPhone: (phone) => {
        // Remove all non-digits
        const digits = phone.replace(/\D/g, '');
        // Accept 10 or 11 digit numbers (with or without country code)
        return digits.length === 10 || digits.length === 11;
    },
    
    // Order items validation
    validateOrderItems: (orderItems, accessories) => {
        const errors = [];
        
        if (!orderItems || orderItems.length === 0) {
            errors.push('At least one order item is required');
        }
        
        orderItems.forEach((item, index) => {
            if (!item.description?.trim()) {
                errors.push(`Order item ${index + 1}: Description is required`);
            }
            
            const price = parseFloat(item.price);
            if (isNaN(price) || price < 0) {
                errors.push(`Order item ${index + 1}: Valid price is required`);
            }
            
            const quantity = parseInt(item.quantity);
            if (isNaN(quantity) || quantity < 1) {
                errors.push(`Order item ${index + 1}: Quantity must be at least 1`);
            }
        });
        
        accessories.forEach((acc, index) => {
            if (!acc.description?.trim() && !acc.name?.trim()) {
                errors.push(`Accessory ${index + 1}: Description or name is required`);
            }
            
            const price = parseFloat(acc.price);
            if (isNaN(price) || price < 0) {
                errors.push(`Accessory ${index + 1}: Valid price is required`);
            }
        });
        
        return {
            isValid: errors.length === 0,
            errors: errors
        };
    },
    
    // Room tasks validation
    validateRoomTasks: (roomTasks) => {
        const errors = [];
        
        if (!roomTasks || roomTasks.length === 0) {
            errors.push('At least one room task is required');
        }
        
        roomTasks.forEach((task, index) => {
            if (!task.roomNo?.trim()) {
                errors.push(`Room task ${index + 1}: Room number is required`);
            }
            
            if (!task.roomTask?.trim()) {
                errors.push(`Room task ${index + 1}: Task description is required`);
            }
            
            if (!task.assignedWorker?.trim()) {
                errors.push(`Room task ${index + 1}: Assigned worker is required`);
            }
            
            if (!task.taskDueDate) {
                errors.push(`Room task ${index + 1}: Due date is required`);
            }
        });
        
        return {
            isValid: errors.length === 0,
            errors: errors
        };
    },


    // Wig Repair must have a real repair line item (its cost may be $0 only when
    // warranty covers it, which is enforced with the wig's warranty on the Wig
    // Details step).
    validateWigRepairHasCharge: ({ customerInfo, orderItems = [], accessories = [] }) => {
        // "Aidel Wig Repair" follows the same repair rules as "Wig Repair".
        if (normalizeOrderType(customerInfo?.orderType) !== 'Wig Repair') {
            return { isValid: true };
        }

        // 'chenw' is exempt from the Wig Repair warranty / $0-total gate.
        // Keep this in sync with the same check in WigDetails.js.
        if (isWarrantyGateExempt()) {
            return { isValid: true };
        }

        // Stock wigs (customer "Stock Wig" / OrderCustomerNo 6252) are internal
        // and never need a priced repair line.
        const isStockWig =
            String(customerInfo?.customerNo ?? '').trim() === '6252' ||
            String(customerInfo?.ID ?? '').trim() === '6252' ||
            String(customerInfo?.customerName ?? '').trim().toLowerCase() === 'stock wig';
        if (isStockWig) {
            return { isValid: true };
        }

        const hasRepairLine = (orderItems || []).some(
            i => i.type === 'repair' || i.type === 'discount'
        );

        if (hasRepairLine) {
            return { isValid: true };
        }

        const total =
            (orderItems || []).reduce((s, i) => s + (parseFloat(i.price || 0) * (parseInt(i.quantity) || 1)), 0) +
            (accessories || []).reduce((s, a) => s + (parseFloat(a.price || 0) * (parseInt(a.quantity) || 1)), 0);

        if (total > 0) {
            return { isValid: true };
        }

        return {
            isValid: false,
            error: 'This wig repair has no cost. Go back to Wig Details and add the repair as a line item (enter $0 only if the wig is under warranty).',
            category: 'items',
        };
    },

    // Validate that order has items
    validateOrderHasItems: ({ orderItems, accessories }) => {
        console.log('🔍 Validating order has items...');
        
        const totalItems = (orderItems?.length || 0) + (accessories?.length || 0);
        
        if (totalItems === 0) {
            console.log('❌ No items in order');
            return {
                isValid: false,
                error: 'Please add at least one item to the order before proceeding.',
                category: 'items'
            };
        }
        
        // Check if items have valid prices
        const invalidItems = [...(orderItems || []), ...(accessories || [])].filter(item => {
            const price = parseFloat(item.price || 0);
            return isNaN(price) || price < 0;
        });
     
        
        console.log('✅ Order items validation passed');
        return { isValid: true };
    },
    
    // Complete order validation
    validateCompleteOrder: ({
        customerInfo,
        orderItems,
        accessories,
        roomTasks,
        paymentCalculations,
        agreementManager,
        isIsraelUser
    }) => {
        console.log('🔍 === COMPLETE ORDER VALIDATION ===');
        
        const validations = [
            validationService.validateCustomerInfo(customerInfo),
            validationService.validateOrderItems(orderItems, accessories),
            validationService.validateRoomTasks(roomTasks),
            validationService.validateAllPayments(paymentCalculations.payments),
            validationService.validateOrder({
                paymentCalculations,
                agreementManager,
                customerInfo,
                isIsraelUser
            })
        ];
        
        const failures = validations.filter(v => !v.isValid);
        
        if (failures.length > 0) {
            const allErrors = failures.flatMap(f => f.errors || [f.error]).filter(Boolean);
            
            return {
                isValid: false,
                errors: allErrors,
                error: allErrors[0], // First error for display
                category: 'complete_order'
            };
        }
        
        console.log('✅ Complete order validation passed');
        return { isValid: true };
    }
};

export default validationService;
